import React, { useContext, useRef } from 'react';
import { Box, Chip, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';

import { SnackBarContext } from '../../../sharedModules/form/contexts/SnackBarContext';
import { LabelMarker } from '../model';

// Label text has to be readable across a warehouse, so the editor needs real point sizes
// rather than Quill's small/large/huge. Keep in sync with LabelConfig::EDITOR_FONT_SIZES,
// which guards the default template against using a size the editor cannot offer.
const FONT_SIZES = ['16px', '24px', '36px', '48px', '60px', '96px'];

// Style attributors, not Quill's default class ones: the stored HTML is printed outside the
// editor, where the ql-* classes carry no styling. Registration is global — the notification
// page's editor offers neither control, so nothing there changes.
const SizeStyle = Quill.import('attributors/style/size');
SizeStyle.whitelist = FONT_SIZES;
Quill.register(SizeStyle, true);
Quill.register(Quill.import('attributors/style/align'), true);

const QUILL_MODULES = {
  toolbar: [
    [{ size: FONT_SIZES }],
    ['bold', 'italic', 'underline'],
    [{ align: '' }, { align: 'center' }, { align: 'right' }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['clean'],
  ],
};

// The size picker renders its options blank unless CSS supplies a label, and snow only ships
// labels for the small/large/huge whitelist it replaces.
const editorSx = {
  mb: 2,
  '& .ql-container': { minHeight: 320, maxHeight: 520, overflowY: 'auto' },
  '& .ql-size .ql-picker-item::before, & .ql-size .ql-picker-label::before': {
    content: 'attr(data-value)',
  },
  '& .ql-size .ql-picker-label:not([data-value])::before': { content: '"Size"' },
  '& .ql-size': { width: 72 },
} as const;

interface LabelTemplateEditorProps {
  body: string;
  markers: LabelMarker[];
  onBodyChange: (value: string, source: string) => void;
}

const LabelTemplateEditor: React.FC<LabelTemplateEditorProps> = ({ body, markers, onBodyChange }) => {
  const { showErrorSnackBar } = useContext(SnackBarContext);
  const quillRef = useRef<ReactQuill>(null);

  // Copy-and-paste is awkward inside a rich editor, so a marker chip drops its text straight
  // at the cursor. With no cursor (the editor was never focused) it goes to the end.
  const handleInsertMarker = (marker: string) => {
    const editor = quillRef.current?.getEditor();
    if (!editor) {
      showErrorSnackBar?.('Click into the template first, then choose a variable.');
      return;
    }
    const index = editor.getSelection(true)?.index ?? editor.getLength() - 1;
    editor.insertText(index, marker, 'user');
    editor.setSelection(index + marker.length, 0);
  };

  return (
    <Box>
      <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1, border: '1px solid #e0e0e0' }}>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
          Template variables — click to insert one where the cursor is
        </Typography>
        {markers.map((variable) => (
          <Tooltip key={variable.marker} title={variable.description}>
            <Chip
              icon={<AddIcon sx={{ fontSize: 14 }} />}
              label={variable.marker}
              onClick={() => handleInsertMarker(variable.marker)}
              clickable
              variant="outlined"
              size="small"
              sx={{ mr: 1, mb: 0.5 }}
            />
          </Tooltip>
        ))}
      </Box>

      <Typography variant="body2" color="textSecondary" gutterBottom>
        Label Template
      </Typography>
      <Box sx={editorSx}>
        <ReactQuill
          ref={quillRef}
          placeholder="Enter the label layout here..."
          theme="snow"
          value={body}
          onChange={(value, _delta, source) => onBodyChange(value, source)}
          modules={QUILL_MODULES}
        />
      </Box>
    </Box>
  );
};

export default LabelTemplateEditor;
